import { useState } from "react";

import { useProjectActions, useProjectContext } from "@contexts/projects";
import { extraValueActiveMutation } from "./ExtraValueAccordion";
import { formatExtraValueCents } from "./ExtraValuesSummary";

const centsOfDocument = (document) =>
  (document.history ?? [])
    .filter((entry) => entry.isValid)
    .reduce(
      (total, entry) =>
        total +
        (Number.isSafeInteger(entry.totalCents)
          ? entry.totalCents
          : Math.round(Number(entry.value ?? 0) * 100)),
      0,
    );

const ExistingExtraValues = ({
  documents,
  readOnly,
  highlightedWbs,
  onDocument,
  onMissingDocument,
  onOpenAi,
  quotaText,
}) => {
  const { updateExtra } = useProjectActions();
  const { cubBased, activeProject } = useProjectContext();
  const [open, setOpen] = useState({});
  const [status, setStatus] = useState(null);
  const format = (cents) =>
    formatExtraValueCents(cents, {
      cubBased,
      indexerValue: activeProject?.indexer?.value,
    });

  const toggleActive = async (document, entry, index) => {
    const response = await updateExtra(
      document.wbs_code_id,
      extraValueActiveMutation(entry, document.history, index),
      document.item_name,
    );
    if (response?.code === 200 && response.document)
      onDocument?.(response.document);
    else if (response?.code === 200) await onMissingDocument?.();
    else
      setStatus(response?.message ?? "Não foi possível atualizar o histórico.");
  };

  return (
    <div className="existing-extra-values">
      <header className="existing-extra-values__header">
        <div className="existing-extra-values__heading">
          <h2>Estouros e economias existentes</h2>
          <p>
            {documents.length
              ? `${documents.length} alvos com histórico`
              : "Nenhum lançamento criado."}
          </p>
        </div>
        {!readOnly ? (
          <div className="existing-extra-values__ai-action">
            <button
              type="button"
              className="button button-accent small"
              onClick={onOpenAi}
            >
              Criar com IA
            </button>
            {quotaText ? (
              <span
                className="existing-extra-values__ai-quota"
                title="A cota de IA é compartilhada com as outras telas da obra."
              >
                {quotaText}
              </span>
            ) : null}
          </div>
        ) : null}
      </header>
      {status ? (
        <p role="alert" className="alert alert-error">
          {status}
        </p>
      ) : null}
      {documents.length === 0 ? (
        <p className="empty-state">
          Busque um alvo na EAP abaixo para criar o primeiro lançamento.
        </p>
      ) : (
        documents.map((document) => {
          const expanded = Boolean(open[document.wbs_code_id]);
          return (
            <article
              key={document._id ?? document.wbs_code_id}
              id={`created-item-${document.wbs_code_id}`}
              className={`existing-extra-values__item${
                highlightedWbs === document.wbs_code_id ? " is-highlighted" : ""
              }`}
            >
              <button
                type="button"
                className="existing-extra-values__summary"
                aria-expanded={expanded}
                aria-controls={`extra-values-history-${document.wbs_code_id}`}
                onClick={() =>
                  setOpen((current) => ({
                    ...current,
                    [document.wbs_code_id]: !expanded,
                  }))
                }
              >
                <span>
                  <strong>{document.item_name ?? document.wbs_code_id}</strong>
                  <small>{document.wbs_code_id}</small>
                </span>
                <span className="existing-extra-values__value">
                  {format(centsOfDocument(document))}
                </span>
                <span className="existing-extra-values__count">
                  {(document.history ?? []).length} registros
                </span>
                <span className="existing-extra-values__chevron" aria-hidden="true">
                  {expanded ? "−" : "+"}
                </span>
              </button>
              {expanded ? (
                <div
                  id={`extra-values-history-${document.wbs_code_id}`}
                  className="existing-extra-values__history"
                >
                  {(document.history ?? []).map((entry, index) => (
                    <div
                      key={entry.entryId ?? index}
                      className={entry.isValid ? "" : "is-inactive"}
                    >
                      <span>{entry.description || "Sem descrição"}</span>
                      <span>
                        {format(
                          Number.isSafeInteger(entry.totalCents)
                            ? entry.totalCents
                            : Math.round(entry.value * 100),
                        )}
                      </span>
                      <span>
                        {entry.start_date} · {entry.installments} parcelas
                      </span>
                      <span>{entry.isValid ? "Ativo" : "Desativado"}</span>
                      {!readOnly ? (
                        <button
                          type="button"
                          className="button button-secondary small"
                          onClick={() => toggleActive(document, entry, index)}
                        >
                          {entry.isValid ? "Desativar" : "Ativar"}
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </article>
          );
        })
      )}
    </div>
  );
};

export default ExistingExtraValues;
